import Avatar from '@/components/Avatar'
import IcoMoon from '@/components/IcoMoon'
import Layout, { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { rbp } from '@/constants/measured-scope'
import { useFeatureToggle } from '@/context/featureToggles'
import { lobbyUrl } from '@/lib/lobbyUrl'
import { ItemType, LivenessType, NavigateToAttendedSessionDetailAction } from '@/shared/api-generated-types'
import ListViewClass from '@/widgets/ListViewClass'
import { useMediaQuery } from '@react-hookz/web'
import dayjs from 'dayjs'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import * as styles from './style'
import BookmarkButton from '@/components/BookmarkButton'
import { useBookmarkItems } from '@/context/bookmarkedItems'

export interface Props {
  title?: string
  imageUrl?: string
  guideAvatarUrl?: string
  start?: string
  end?: string
  navigateToAttendedSessionDetailAction?: NavigateToAttendedSessionDetailAction
  onClick?: () => void
  guideByline?: string
  sessionId?: string
  livenessType?: LivenessType
  bookmarked?: boolean
  classId?: string
}

export const Divider = (): JSX.Element => <hr className={styles.divider} />

const AttendedSessionItem = ({
  title,
  imageUrl,
  guideAvatarUrl,
  navigateToAttendedSessionDetailAction,
  start,
  end,
  guideByline,
  sessionId,
  livenessType,
  bookmarked,
  classId,
}: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const { state: bookmarkState } = useBookmarkItems()
  const isNewHorizontalClassCard = useFeatureToggle('exp_horizontal_class_card')
  const showClassNotesFeedbackLinks = useFeatureToggle('class_notes_feedback_links')
  const isMobile = useMediaQuery(`only screen and (max-width : ${rbp.sm})`, { initializeWithValue: false })
  const feedbackLink = lobbyUrl(`/session/${sessionId}/feedback?source=class_notes`)
  const [isBookmarked, setIsBookmarked] = useState<boolean | undefined>(bookmarked)

  const showTimeForDesktopUser = useMemo(() => {
    if (isMobile) {
      return false
    }
    return true
  }, [isMobile])

  useEffect(() => {
    if (classId && bookmarkState.classes?.[classId] === true) {
      setIsBookmarked(true)
    } else if (classId && bookmarkState.classes?.[classId] === false) {
      setIsBookmarked(false)
    }
  }, [bookmarkState, classId])

  return (
    <>
      <Link href={navigateToAttendedSessionDetailAction?.url ?? '/'} className={styles.root}>
        {isNewHorizontalClassCard ? (
          <ListViewClass
            title={title}
            imageUrl={imageUrl}
            guideAvatarUrl={guideAvatarUrl}
            start={start}
            end={end}
            guideByline={guideByline}
            zeroVerticalSpacing
            isNotes
            sessionId={sessionId}
            actionText={t('classNotes.viewNotes')}
            feedbackText={t('feedback.ask.label')}
            livenessType={livenessType}
          />
        ) : (
          <Layout spacing="sm">
            <Stack x justifyContent="center" alignItems="center">
              {imageUrl ? (
                <Stack y className={styles.imageWrapper} alignSelf="flex-start">
                  <div className={styles.image}>
                    <img src={imageUrl} alt={title} loading="lazy" />
                    <BookmarkButton
                      bookmarked={isBookmarked}
                      size="sm"
                      itemId={classId}
                      itemType={ItemType.Class}
                      className={styles.bookmarkDescriptor}
                    />
                  </div>
                </Stack>
              ) : null}
              <Stack y className={styles.text}>
                {title ? (
                  <Text
                    size="xxs"
                    responsiveSize={{ sm: 'xs', lg: 'md' }}
                    weight="lg"
                    tag="strong"
                    className={styles.truncate}
                  >
                    {title}
                  </Text>
                ) : null}
                {guideAvatarUrl ? (
                  <Text
                    size="xxxs"
                    responsiveSize={{ sm: 'xxxs', lg: 'xs' }}
                    weight="sm"
                    className={`${styles.truncate} ${styles.subtitle}`}
                  >
                    <Avatar size="xs" src={guideAvatarUrl} className={styles.guidePhoto} />
                    {guideByline}
                  </Text>
                ) : null}

                {showTimeForDesktopUser === false ? (
                  <Stack x alignItems="center">
                    <Stack x className={styles.detailsContainer} alignItems="center">
                      <IcoMoon className={`mr-xxs ${styles.calendarIcon}`} size={16} icon={'calendar'} />
                      <Text size="xxxs" responsiveSize={{ sm: 'xxxs', lg: 'xs' }} weight="lg">
                        {dayjs(start).format('ddd D MMM,')}&nbsp;
                      </Text>
                      <Text size="xxxs" responsiveSize={{ sm: 'xxxs', lg: 'xs' }} weight="sm">
                        {dayjs(start).format('hh:mma')}
                      </Text>
                    </Stack>

                    <a>
                      <Text size="xxxs" responsiveSize={{ sm: 'xxs', lg: 'xs' }} weight="sb" className={styles.notes}>
                        {t('classNotes.viewNotes')}
                      </Text>
                    </a>
                  </Stack>
                ) : (
                  <Stack x justifyContent="stretch" alignItems="center">
                    <Stack x className={styles.detailsContainer} alignItems="center">
                      <IcoMoon className={`mr-xxs ${styles.calendarIcon}`} size={20} icon={'calendar'} />
                      <Text size="xxxs" responsiveSize={{ sm: 'xxxs', lg: 'xs' }} weight="lg">
                        {dayjs(start).format('ddd D MMM,')}&nbsp;
                      </Text>
                      <Text size="xxxs" responsiveSize={{ sm: 'xxxs', lg: 'xs' }} weight="sm">
                        {dayjs(start).format('hh:mma')} - {dayjs(end).format('hh:mma')}
                      </Text>
                    </Stack>

                    <Stack x alignItems="center" justifyContent="flex-end" spaceBetweenX="sm">
                      {showClassNotesFeedbackLinks && (
                        <span
                          className="mr-xs"
                          onClick={(event) => {
                            event.preventDefault()
                            window.open(feedbackLink, '_blank')
                          }}
                        >
                          <Text
                            className={styles.notes}
                            size="xxxs"
                            responsiveSize={{ sm: 'xxs', lg: 'xs' }}
                            weight="sb"
                          >
                            {t('feedback.ask.label')}
                          </Text>
                        </span>
                      )}
                      <Text size="xxxs" responsiveSize={{ sm: 'xxs', lg: 'xs' }} weight="sb" className={styles.notes}>
                        {t('classNotes.viewNotes')}
                      </Text>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Layout>
        )}
      </Link>
      <Divider />
    </>
  )
}

export default AttendedSessionItem
